import React from "react";
import { Column, Heading, Row, Section, TruncatedText } from "@hex-insights/core";
import { useActivePageRegistration } from "@hex-insights/router";
import {
	ButtonLink,
	Icon,
	MedicalConditionConditionType,
	PersonFormat,
	StudentComponents,
	StudentFormat,
	StudentMedicalConditionComponents,
	StudentMedicalConditionUtilities,
	StudentOTCMedicationComponents,
	StudentPrescriptionMedicationComponents,
	StudentVaccinationRecordComponents,
	useReEnrollmentStudentHealthDetailQuery,
} from "@hex-insights/verita-romania.shared";
import { ReEnrollmentLayout } from "../../Components";
import { reEnrollmentStudentHealthIndexPageInfo } from "../IndexPage/pageinfo";
import { reEnrollmentStudentHealthDetailPageInfo } from "./pageinfo";
import generalStyles from "../../general.module.css";
import styles from "./styles.module.css";

export function ReEnrollmentStudentHealthDetailPage() {
	const { studentID } = reEnrollmentStudentHealthDetailPageInfo.useParams();

	const { data } = useReEnrollmentStudentHealthDetailQuery({ variables: { id: studentID } });
	const student = data?.student;

	useActivePageRegistration(
		reEnrollmentStudentHealthDetailPageInfo,
		student ? reEnrollmentStudentHealthDetailPageInfo.title(student) : "",
	);

	const { allergies, foodIntolerances, medicalConditions } = React.useMemo(
		() => StudentMedicalConditionUtilities.splitByConditionType(student?.studentMedicalConditions ?? []),
		[student],
	);

	return (
		<ReEnrollmentLayout
			footerContent={
				<Row justify="space-between" align="center">
					<ButtonLink variant="link" to={reEnrollmentStudentHealthIndexPageInfo.to}>
						<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
							<Icon.ArrowLeft size={24} /> Back
						</Row>
					</ButtonLink>

					<ButtonLink variant="primary" to={reEnrollmentStudentHealthIndexPageInfo.to}>
						Save
					</ButtonLink>
				</Row>
			}
		>
			<div style={{ width: "fit-content", margin: "0 auto" }}>
				<Heading.H1 className={generalStyles["heading"]}>Student Health Information</Heading.H1>

				{student && (
					<Column justify="spaced-start" verticalSpacing="1rem">
						<Row justify="spaced-start" align="center" horizontalSpacing="1rem" style={{ minWidth: 0 }}>
							<StudentComponents.StudentIcon student={student} size={80} />

							<Column justify="spaced-start" verticalSpacing="0.25rem" style={{ minWidth: 0 }}>
								<TruncatedText className={styles["student-name"]} style={{ minWidth: 0 }}>
									{StudentFormat.name(student)}
								</TruncatedText>
								<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
									<Icon.PersonArmsSpread size={18} color="var(--neutral-colors--tertiary)" />
									<TruncatedText className={styles["student-age"]}>
										{PersonFormat.age(student.person.dateOfBirth)}
									</TruncatedText>
								</Row>
							</Column>
						</Row>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Allergies</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentMedicalConditionComponents.StudentMedicalConditionEditableStudentList
										student={student}
										studentMedicalConditions={allergies}
										conditionType={MedicalConditionConditionType.Allergy}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Food Intolerances</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentMedicalConditionComponents.StudentMedicalConditionEditableStudentList
										student={student}
										studentMedicalConditions={foodIntolerances}
										conditionType={MedicalConditionConditionType.FoodIntolerance}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Medical Conditions</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentMedicalConditionComponents.StudentMedicalConditionEditableStudentList
										student={student}
										studentMedicalConditions={medicalConditions}
										conditionType={MedicalConditionConditionType.MedicalCondition}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>OTC Medications</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentOTCMedicationComponents.StudentOTCMedicationEditableStudentList
										student={student}
										studentOTCMedications={student.studentOtcMedications}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Prescription Medications</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentPrescriptionMedicationComponents.StudentPrescriptionMedicationEditableStudentList
										student={student}
										studentPrescriptionMedications={student.studentPrescriptionMedications}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>

						<Section>
							<Section.Header>
								<Heading.H2 noMargin>Vaccination Records</Heading.H2>
							</Section.Header>
							<Section.Body>
								<div style={{ maxWidth: "30rem" }}>
									<StudentVaccinationRecordComponents.StudentVaccinationRecordEditableStudentList
										student={student}
										studentVaccinationRecords={student.studentVaccinationRecords}
										emptyViewVariant="slim"
									/>
								</div>
							</Section.Body>
						</Section>
					</Column>
				)}
			</div>
		</ReEnrollmentLayout>
	);
}
